import * as THREE from 'three'
import { AppObject, LogoPlacement, OcctImportMesh } from '../types'
import { LogoOutline, cachedLogoOutline } from './logoOutline'
import { findBodyObject } from './patternUV'

/**
 * Logo parts
 * ----------
 * A "pattern" is printed into a part's base-color texture — it lives in UV space and
 * cannot be picked, named or measured. A "logo" is instead a REAL part: a curved sheet
 * of geometry generated to sit on the model's surface, with the artwork as its own
 * texture. It appears in the outliner, can be renamed (e.g. `logo_main`), moved with
 * millimetre precision and is exported as a named node — which is what the downstream
 * product-profile logic keys off.
 *
 * Conforming to the surface
 * -------------------------
 * Ray-casting every sheet vertex against the target mesh is far too slow to drive a
 * slider, so instead we build a RADIUS FIELD once per part: a (theta, y) grid holding
 * the largest distance from the model's vertical axis to any of its vertices. Building
 * it is one pass over the positions; sampling it is a bilinear lookup. The field uses
 * the same axis convention as the pattern UVs and the Product Profile wrap shader
 * (`theta = atan2(x - cx, z - cz)`, 0 = front/+Z), so a logo at angle 0 lands on the
 * same spot as pattern artwork at u = 0.5.
 */

const TWO_PI = Math.PI * 2
export const DEG = Math.PI / 180

const THETA_BINS = 256
const Y_BINS = 256
/** Columns across the sheet; rows are half this. Fine enough that a wrap reads smooth. */
export const DEFAULT_SEGMENTS = 96

export interface SurfaceField {
  /** Vertical axis of the part (XZ midpoint of its bounds). */
  cx: number
  cz: number
  minY: number
  maxY: number
  /** Fallback radius when the field has no data at all. */
  meanRadius: number
  /** THETA_BINS x Y_BINS grid of distances from the axis (row-major by y). */
  radii: Float32Array
}

// Keyed on the mesh (which is replaced whenever geometry changes), so the field is
// built once per part and reused across every placement edit.
const fieldCache = new WeakMap<OcctImportMesh, SurfaceField>()

/** Build (or reuse) the radius field of a part. */
export function surfaceFieldOf(object: AppObject): SurfaceField | null {
  const cached = fieldCache.get(object.mesh)
  if (cached) return cached

  const pos = object.mesh.attributes.position?.array
  if (!pos || pos.length < 9) return null

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  for (let i = 0; i < pos.length; i += 3) {
    if (pos[i] < minX) minX = pos[i]
    if (pos[i] > maxX) maxX = pos[i]
    if (pos[i + 1] < minY) minY = pos[i + 1]
    if (pos[i + 1] > maxY) maxY = pos[i + 1]
    if (pos[i + 2] < minZ) minZ = pos[i + 2]
    if (pos[i + 2] > maxZ) maxZ = pos[i + 2]
  }

  const cx = (minX + maxX) / 2
  const cz = (minZ + maxZ) / 2
  const height = Math.max(maxY - minY, 1e-6)

  const radii = new Float32Array(THETA_BINS * Y_BINS).fill(-1)
  let radiusSum = 0
  let radiusCount = 0

  for (let i = 0; i < pos.length; i += 3) {
    const dx = pos[i] - cx
    const dz = pos[i + 2] - cz
    const r = Math.hypot(dx, dz)
    const theta = Math.atan2(dx, dz)
    let t = Math.floor(((theta + Math.PI) / TWO_PI) * THETA_BINS)
    if (t < 0) t = 0
    else if (t >= THETA_BINS) t = THETA_BINS - 1
    let y = Math.floor(((pos[i + 1] - minY) / height) * Y_BINS)
    if (y < 0) y = 0
    else if (y >= Y_BINS) y = Y_BINS - 1
    const k = y * THETA_BINS + t
    // The outermost surface wins: a ray leaving the axis exits through it.
    if (r > radii[k]) radii[k] = r
    radiusSum += r
    radiusCount++
  }

  fillHoles(radii)
  smoothField(radii)

  const field: SurfaceField = {
    cx,
    cz,
    minY,
    maxY,
    meanRadius: radiusCount ? radiusSum / radiusCount : Math.max(maxX - minX, maxZ - minZ) / 2 || 1,
    radii,
  }
  fieldCache.set(object.mesh, field)
  return field
}

/**
 * A coarse mesh leaves bins with no vertex in them. Fill each row by interpolating
 * around the circle between its nearest filled bins, then copy whole empty rows from
 * the nearest row that has data.
 */
function fillHoles(radii: Float32Array): void {
  const prev = new Int32Array(THETA_BINS)
  const next = new Int32Array(THETA_BINS)
  const rowFilled: boolean[] = []

  for (let y = 0; y < Y_BINS; y++) {
    const base = y * THETA_BINS
    let any = false
    for (let t = 0; t < THETA_BINS; t++) {
      if (radii[base + t] >= 0) {
        any = true
        break
      }
    }
    rowFilled.push(any)
    if (!any) continue

    // Two circular sweeps record the nearest filled bin on each side.
    let last = -1
    for (let n = 0; n < THETA_BINS * 2; n++) {
      const t = n % THETA_BINS
      if (radii[base + t] >= 0) last = t
      if (n >= THETA_BINS) prev[t] = last
    }
    last = -1
    for (let n = THETA_BINS * 2 - 1; n >= 0; n--) {
      const t = n % THETA_BINS
      if (radii[base + t] >= 0) last = t
      if (n < THETA_BINS) next[t] = last
    }

    for (let t = 0; t < THETA_BINS; t++) {
      if (radii[base + t] >= 0) continue
      const a = prev[t]
      const b = next[t]
      if (a < 0 || b < 0) continue
      const da = (t - a + THETA_BINS) % THETA_BINS
      const db = (b - t + THETA_BINS) % THETA_BINS
      const w = da + db === 0 ? 0 : da / (da + db)
      radii[base + t] = radii[base + a] * (1 - w) + radii[base + b] * w
    }
  }

  for (let y = 0; y < Y_BINS; y++) {
    if (rowFilled[y]) continue
    let src = -1
    for (let d = 1; d < Y_BINS; d++) {
      if (y - d >= 0 && rowFilled[y - d]) {
        src = y - d
        break
      }
      if (y + d < Y_BINS && rowFilled[y + d]) {
        src = y + d
        break
      }
    }
    if (src < 0) continue
    radii.copyWithin(y * THETA_BINS, src * THETA_BINS, (src + 1) * THETA_BINS)
    rowFilled[y] = true
  }
}

/**
 * Take the staircase out of the field. Each bin holds the largest radius of the
 * vertices that landed in it, which biases every bin toward the widest point it covers
 * and leaves a small step between neighbours. Sampled by a logo only a few bins tall,
 * those steps become visible ripples, so the grid is blurred (wrapping around theta,
 * clamped in y) before anything reads it.
 */
function smoothField(radii: Float32Array): void {
  const tmp = new Float32Array(radii.length)
  for (let pass = 0; pass < 2; pass++) {
    for (let y = 0; y < Y_BINS; y++) {
      const base = y * THETA_BINS
      for (let t = 0; t < THETA_BINS; t++) {
        const l = radii[base + ((t - 1 + THETA_BINS) % THETA_BINS)]
        const c = radii[base + t]
        const r = radii[base + ((t + 1) % THETA_BINS)]
        tmp[base + t] = ((l < 0 ? c : l) + 2 * c + (r < 0 ? c : r)) / 4
      }
    }
    for (let y = 0; y < Y_BINS; y++) {
      const base = y * THETA_BINS
      const up = Math.max(0, y - 1) * THETA_BINS
      const down = Math.min(Y_BINS - 1, y + 1) * THETA_BINS
      for (let t = 0; t < THETA_BINS; t++) {
        const c = tmp[base + t]
        const u = tmp[up + t]
        const d = tmp[down + t]
        radii[base + t] = ((u < 0 ? c : u) + 2 * c + (d < 0 ? c : d)) / 4
      }
    }
  }
}

/** Distance from the axis to the surface at (theta, y), bilinearly interpolated. */
export function radiusAt(field: SurfaceField, theta: number, y: number): number {
  const height = Math.max(field.maxY - field.minY, 1e-6)
  let tf = ((((theta + Math.PI) / TWO_PI) % 1) + 1) % 1
  tf = tf * THETA_BINS - 0.5
  let t0 = Math.floor(tf)
  const ft = tf - t0
  t0 = ((t0 % THETA_BINS) + THETA_BINS) % THETA_BINS
  const t1 = (t0 + 1) % THETA_BINS

  let yf = ((y - field.minY) / height) * Y_BINS - 0.5
  yf = Math.max(0, Math.min(Y_BINS - 1, yf))
  const y0 = Math.floor(yf)
  const fy = yf - y0
  const y1 = Math.min(Y_BINS - 1, y0 + 1)

  const pick = (yi: number, ti: number) => {
    const v = field.radii[yi * THETA_BINS + ti]
    return v >= 0 ? v : field.meanRadius
  }
  const a = pick(y0, t0) * (1 - ft) + pick(y0, t1) * ft
  const b = pick(y1, t0) * (1 - ft) + pick(y1, t1) * ft
  return a * (1 - fy) + b * fy
}

/** The part a logo sits on: its explicit target, or the largest part in the model. */
export function logoTargetOf(objects: AppObject[], placement: LogoPlacement): AppObject | null {
  const candidates = objects.filter((o) => !o.logo)
  const explicit = placement.targetId ? candidates.find((o) => o.id === placement.targetId) : null
  if (explicit) return explicit
  return outermostPartAt(candidates, placement.centerY)
}

/**
 * The part a logo should sit on at height `y`: the one whose surface is FURTHEST from the
 * axis there — the one a viewer actually sees.
 *
 * Size is the wrong measure. Drinkware is a shell inside a shell, and on a vacuum tumbler
 * the inner liner is easily the biggest part by bounding box while being several
 * millimetres inside the wall; a logo wrapped onto it is sealed inside the product, drawn
 * but never visible. Height matters too: a part that does not reach the logo puts it at a
 * radius measured somewhere else entirely.
 */
export function outermostPartAt(candidates: AppObject[], y: number): AppObject | null {
  let best: AppObject | null = null
  let bestRadius = -Infinity
  for (const o of candidates) {
    const field = surfaceFieldOf(o)
    if (!field || y < field.minY || y > field.maxY) continue
    // The widest point around the axis at this height: what the logo has to sit on top of.
    let radius = 0
    for (let i = 0; i < 32; i++) radius = Math.max(radius, radiusAt(field, (i / 32) * TWO_PI, y))
    if (radius > bestRadius) {
      bestRadius = radius
      best = o
    }
  }
  // Nothing reaches that height (a logo left hanging above or below the model): fall back
  // to the biggest part rather than losing the target altogether.
  return best ?? findBodyObject(candidates)
}

/**
 * Extra lift so the sheet clears every part, not just the one it is wrapped onto.
 *
 * The wrap target sets the curvature the logo follows, but it is not always the outermost
 * thing at that spot — an explicitly chosen inner part, or a band that another part covers.
 * Without this the logo is drawn inside the product and simply never seen.
 */
function clearanceLift(objects: AppObject[], placement: LogoPlacement, target: AppObject | null): number {
  const field = target ? surfaceFieldOf(target) : null
  if (!field) return 0
  const angle = placement.angleDeg * DEG
  const own = radiusAt(field, angle, placement.centerY)
  let outermost = own
  for (const o of objects) {
    if (o.logo || o === target) continue
    const other = surfaceFieldOf(o)
    if (!other || placement.centerY < other.minY || placement.centerY > other.maxY) continue
    // Parts of one product share an axis closely enough for this comparison; the exact
    // figure only decides how much to lift, and lifting slightly too much is invisible.
    outermost = Math.max(outermost, radiusAt(other, angle, placement.centerY))
  }
  return Math.max(0, outermost - own)
}

export interface LogoMeshData {
  position: Float32Array
  normal: Float32Array
  uv: Float32Array
  index: Uint32Array
}

/**
 * Generate the logo sheet. The grid is laid out in the artwork's own frame
 * (a = across, b = up), rotated by `rotationDeg`, then wrapped onto the surface:
 * `a` becomes arc length around the axis and `b` becomes height.
 */
export function buildLogoMeshData(placement: LogoPlacement, field: SurfaceField | null): LogoMeshData {
  const segs = Math.max(2, Math.round(placement.segments ?? DEFAULT_SEGMENTS))
  const conform = placement.conform && !!field
  const cols = conform ? segs : 2
  const rows = conform ? Math.max(2, Math.round(segs / 2)) : 2

  const angle = placement.angleDeg * DEG
  const thickness = Math.max(0, placement.thickness || 0)
  const outerLift = placement.standoff + thickness

  // Radius at the logo's center: converts arc length to an angle and positions a flat
  // (non-conforming) sheet. Measured on the face the user sees, so the artwork ends up
  // exactly `width` across on the outside of the sheet rather than on the bare surface.
  const baseRadius = field ? Math.max(radiusAt(field, angle, placement.centerY), 1e-6) : 1
  const arcRadius = baseRadius + outerLift

  const uv = buildUvGrid(cols, rows)
  const outer = buildSurfaceGrid(placement, field, conform, cols, rows, angle, arcRadius, outerLift)
  const normal = gridNormals(outer, cols, rows)
  smoothGridNormals(normal, cols, rows)

  if (thickness <= 0) {
    return { position: outer, normal, uv, index: gridIndices(cols, rows, 0, false) }
  }

  // Extruded: outer face, inner face (flipped), and a rim joining their borders.
  const inner = buildSurfaceGrid(placement, field, conform, cols, rows, angle, arcRadius, placement.standoff)
  const innerNormal = new Float32Array(normal.length)
  for (let i = 0; i < normal.length; i++) innerNormal[i] = -normal[i]

  const vCount = (cols + 1) * (rows + 1)
  const position = concatF32(outer, inner)
  const normals = concatF32(normal, innerNormal)
  const uvs = concatF32(uv, uv)
  const indices = concatU32(gridIndices(cols, rows, 0, false), gridIndices(cols, rows, vCount, true))

  const rim = buildRim(position, normals, uvs, cols, rows, vCount)
  return {
    position: concatF32(position, rim.position),
    normal: concatF32(normals, rim.normal),
    uv: concatF32(uvs, rim.uv),
    index: concatU32(indices, rim.index),
  }
}

/**
 * Where a point of the artwork (u across, v DOWN — the texture's own frame) lands in the
 * world, lifted `lift` off the part.
 *
 * The grid sheet has always done this inline; the traced outline needs the same mapping at
 * arbitrary points, so it lives here as a function. Same maths, same smooth-patch fit, so a
 * cut-to-shape logo sits exactly where the rectangular one did.
 */
type SheetMapper = (u: number, v: number, lift: number) => [number, number, number]

function makeSheetMapper(
  placement: LogoPlacement,
  field: SurfaceField | null,
  conform: boolean,
  angle: number,
  arcRadius: number
): SheetMapper {
  const cosR = Math.cos(placement.rotationDeg * DEG)
  const sinR = Math.sin(placement.rotationDeg * DEG)
  const cx = field?.cx ?? 0
  const cz = field?.cz ?? 0
  // The artwork's own frame: `a` across the surface, `b` up it, after the in-plane rotation.
  const local = (u: number, v: number): [number, number] => {
    const a0 = (u - 0.5) * placement.width
    const b0 = (0.5 - v) * placement.height
    return [a0 * cosR - b0 * sinR, a0 * sinR + b0 * cosR]
  }

  if (!conform || !field) {
    const dirX = Math.sin(angle)
    const dirZ = Math.cos(angle)
    const rightX = Math.cos(angle)
    const rightZ = -Math.sin(angle)
    const base = field ? Math.max(radiusAt(field, angle, placement.centerY), 1e-6) : 1
    return (u, v, lift) => {
      const [a, b] = local(u, v)
      const radius = base + lift
      return [cx + dirX * radius + rightX * a, placement.centerY + b, cz + dirZ * radius + rightZ * a]
    }
  }

  // One smooth patch fitted through the part's radii under the sheet — see buildSurfaceGrid
  // for why the raw mesh is not sampled directly.
  const n = Math.max(2, Math.round(placement.segments ?? DEFAULT_SEGMENTS))
  const count = (n + 1) * (n + 1)
  const dth = new Float64Array(count)
  const dyy = new Float64Array(count)
  const raw = new Float64Array(count)
  for (let j = 0; j <= n; j++) {
    for (let i = 0; i <= n; i++) {
      const k = j * (n + 1) + i
      const [a, b] = local(i / n, j / n)
      dth[k] = a / arcRadius
      dyy[k] = b
      raw[k] = radiusAt(field, angle + dth[k], placement.centerY + b)
    }
  }
  const surface = fitRadiusPatch(dth, dyy, raw)
  let clearance = 0
  if (surface) {
    for (let k = 0; k < count; k++) clearance = Math.max(clearance, raw[k] - surface(dth[k], dyy[k]))
  }

  return (u, v, lift) => {
    const [a, b] = local(u, v)
    const t = a / arcRadius
    const y = placement.centerY + b
    const r = (surface ? surface(t, b) + clearance : radiusAt(field, angle + t, y)) + lift
    const theta = angle + t
    return [cx + Math.sin(theta) * r, y, cz + Math.cos(theta) * r]
  }
}

/**
 * Build the logo as its own SILHOUETTE: the traced outline of the artwork, triangulated
 * with its holes and wrapped onto the part, instead of a rectangle that shows its shape
 * through the texture's transparency.
 *
 * The shape is then in the geometry, where nothing can ignore it — Blender's solid
 * viewport, a viewer that drops alpha, or the old customizer swapping in its own steel
 * material all still see the mark rather than a box. The texture is unchanged and still
 * colours it, so anti-aliased edges stay smooth.
 *
 * Returns null when the outline has nothing to triangulate; the caller falls back to the
 * rectangular sheet.
 */
export function buildOutlineMeshData(
  placement: LogoPlacement,
  field: SurfaceField | null,
  outline: LogoOutline
): LogoMeshData | null {
  const conform = placement.conform && !!field
  const angle = placement.angleDeg * DEG
  const thickness = Math.max(0, placement.thickness || 0)
  const outerLift = placement.standoff + thickness
  const baseRadius = field ? Math.max(radiusAt(field, angle, placement.centerY), 1e-6) : 1
  const map = makeSheetMapper(placement, field, conform, angle, baseRadius + outerLift)

  // Flatten every shape into one vertex list, remembering each loop so its wall can be
  // built later. Triangulation runs in a y-up reading of the UVs, which is the frame
  // three's triangulator (and its outer-CCW/holes-CW convention) expects.
  const uvs: number[][] = []
  const triangles: number[] = []
  const loops: { start: number; count: number }[] = []
  for (const shape of outline.shapes) {
    const contour = shape.outer.map(([u, v]) => new THREE.Vector2(u, 1 - v))
    const holes = shape.holes.map((hole) => hole.map(([u, v]) => new THREE.Vector2(u, 1 - v)))
    const faces = THREE.ShapeUtils.triangulateShape(contour, holes)
    const base = uvs.length
    for (const loop of [contour, ...holes]) {
      loops.push({ start: uvs.length, count: loop.length })
      for (const p of loop) uvs.push([p.x, 1 - p.y])
    }
    for (const f of faces) triangles.push(base + f[0], base + f[1], base + f[2])
  }
  if (!triangles.length) return null

  const count = uvs.length
  const outer = new Float32Array(count * 3)
  const normal = new Float32Array(count * 3)
  const uv = new Float32Array(count * 2)
  // Normals from the mapped surface itself rather than from the triangles: the outline's
  // triangles are long and thin wherever the artwork is, and their face normals would
  // shade the mark unevenly.
  const E = 1e-3
  for (let i = 0; i < count; i++) {
    const [u, v] = uvs[i]
    const p = map(u, v, outerLift)
    const du = sub(map(Math.min(1, u + E), v, outerLift), map(Math.max(0, u - E), v, outerLift))
    const dv = sub(map(u, Math.min(1, v + E), outerLift), map(u, Math.max(0, v - E), outerLift))
    // dv runs DOWN the sheet, so cross(dv, du) points away from the part.
    const n = normalize(cross(dv, du))
    outer[i * 3] = p[0]
    outer[i * 3 + 1] = p[1]
    outer[i * 3 + 2] = p[2]
    normal[i * 3] = n[0]
    normal[i * 3 + 1] = n[1]
    normal[i * 3 + 2] = n[2]
    uv[i * 2] = u
    uv[i * 2 + 1] = v
  }

  if (thickness <= 0) {
    return { position: outer, normal, uv, index: Uint32Array.from(triangles) }
  }

  // Raised badge: the same outline at the surface, joined to the raised one by walls that
  // follow the artwork's edge — the sides of the letters, not the sides of a slab.
  const inner = new Float32Array(count * 3)
  const innerNormal = new Float32Array(count * 3)
  for (let i = 0; i < count; i++) {
    const [u, v] = uvs[i]
    const p = map(u, v, placement.standoff)
    inner[i * 3] = p[0]
    inner[i * 3 + 1] = p[1]
    inner[i * 3 + 2] = p[2]
    innerNormal[i * 3] = -normal[i * 3]
    innerNormal[i * 3 + 1] = -normal[i * 3 + 1]
    innerNormal[i * 3 + 2] = -normal[i * 3 + 2]
  }

  const faceCount = count * 2
  const wallPos: number[] = []
  const wallNor: number[] = []
  const wallUv: number[] = []
  const wallIdx: number[] = []
  const at = (arr: Float32Array, i: number): [number, number, number] => [arr[i * 3], arr[i * 3 + 1], arr[i * 3 + 2]]
  for (const loop of loops) {
    for (let i = 0; i < loop.count; i++) {
      const a = loop.start + i
      const b = loop.start + ((i + 1) % loop.count)
      const oa = at(outer, a)
      const ob = at(outer, b)
      const ia = at(inner, a)
      const ib = at(inner, b)
      // Each loop is wound with its material on one side (outer CCW, holes CW), so walking
      // it in its own direction faces every wall away from the material.
      const nrm = normalize(cross(sub(ob, oa), sub(ia, oa)))
      const base = faceCount + wallPos.length / 3
      for (const [p, k] of [
        [oa, a],
        [ob, b],
        [ib, b],
        [ia, a],
      ] as [number[], number][]) {
        wallPos.push(p[0], p[1], p[2])
        wallNor.push(nrm[0], nrm[1], nrm[2])
        wallUv.push(uvs[k][0], uvs[k][1])
      }
      wallIdx.push(base, base + 1, base + 2, base, base + 2, base + 3)
    }
  }

  const innerTriangles: number[] = []
  for (let i = 0; i < triangles.length; i += 3) {
    innerTriangles.push(count + triangles[i], count + triangles[i + 2], count + triangles[i + 1])
  }

  return {
    position: concatF32(concatF32(outer, inner), Float32Array.from(wallPos)),
    normal: concatF32(concatF32(normal, innerNormal), Float32Array.from(wallNor)),
    uv: concatF32(concatF32(uv, uv), Float32Array.from(wallUv)),
    index: concatU32(
      concatU32(Uint32Array.from(triangles), Uint32Array.from(innerTriangles)),
      Uint32Array.from(wallIdx)
    ),
  }
}

const sub = (a: number[], b: number[]): [number, number, number] => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]

const cross = (a: number[], b: number[]): [number, number, number] => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
]

function normalize(v: [number, number, number]): [number, number, number] {
  const len = Math.hypot(v[0], v[1], v[2]) || 1
  return [v[0] / len, v[1] / len, v[2] / len]
}

/** Uniform UV grid — flipY:false textures put v = 0 on the top row of the image. */
function buildUvGrid(cols: number, rows: number): Float32Array {
  const uv = new Float32Array((cols + 1) * (rows + 1) * 2)
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= cols; i++) {
      const k = j * (cols + 1) + i
      uv[k * 2] = i / cols
      uv[k * 2 + 1] = j / rows
    }
  }
  return uv
}

/**
 * Sample the sheet onto the surface, lifted `lift` along the RADIAL direction.
 *
 * Lifting radially — rather than along each vertex's own computed normal — is what
 * keeps the artwork undistorted. Per-vertex normals wobble slightly wherever the
 * sampled surface does, and displacing along them nudges vertices sideways within the
 * sheet by different amounts, which shears the letters. Radially, every vertex keeps
 * exactly the angle and height the grid asked for, so the texture stays regular no
 * matter how rough the underlying mesh is. The gap narrows by cos(wall taper) — under
 * half a percent on drinkware — which is not worth the distortion it would cost.
 */
function buildSurfaceGrid(
  placement: LogoPlacement,
  field: SurfaceField | null,
  conform: boolean,
  cols: number,
  rows: number,
  angle: number,
  arcRadius: number,
  lift: number
): Float32Array {
  const cosR = Math.cos(placement.rotationDeg * DEG)
  const sinR = Math.sin(placement.rotationDeg * DEG)
  const cx = field?.cx ?? 0
  const cz = field?.cz ?? 0
  const count = (cols + 1) * (rows + 1)
  const position = new Float32Array(count * 3)

  if (!conform || !field) {
    const dirX = Math.sin(angle)
    const dirZ = Math.cos(angle)
    const rightX = Math.cos(angle)
    const rightZ = -Math.sin(angle)
    const radius = Math.max(radiusAt(field!, angle, placement.centerY), 1e-6) + lift
    for (let j = 0; j <= rows; j++) {
      for (let i = 0; i <= cols; i++) {
        const k = j * (cols + 1) + i
        const a0 = (i / cols - 0.5) * placement.width
        const b0 = (0.5 - j / rows) * placement.height
        const a = a0 * cosR - b0 * sinR
        const b = a0 * sinR + b0 * cosR
        position[k * 3] = cx + dirX * radius + rightX * a
        position[k * 3 + 1] = placement.centerY + b
        position[k * 3 + 2] = cz + dirZ * radius + rightZ * a
      }
    }
    return position
  }

  // Where each vertex sits on the part, and how far out the mesh actually is there.
  const dth = new Float64Array(count)
  const dyy = new Float64Array(count)
  const raw = new Float64Array(count)
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= cols; i++) {
      const k = j * (cols + 1) + i
      const a0 = (i / cols - 0.5) * placement.width
      const b0 = (0.5 - j / rows) * placement.height
      const a = a0 * cosR - b0 * sinR
      const b = a0 * sinR + b0 * cosR
      dth[k] = a / arcRadius
      dyy[k] = b
      raw[k] = radiusAt(field, angle + dth[k], placement.centerY + b)
    }
  }

  // A decal is a smooth piece of material, not a rubbing of the mesh underneath it.
  // Sampling the wall directly copies its every imperfection into the sheet — on a
  // real part that is a tenth of a millimetre of out-of-roundness, enough to ripple
  // the surface visibly at a grazing angle. So fit one smooth patch through the
  // samples instead, and push it out past the roughest sample so it still clears the
  // wall everywhere. Quadratic: enough to follow a taper or a gentle bulge, far too
  // stiff to follow noise.
  const surface = fitRadiusPatch(dth, dyy, raw)
  let clearance = 0
  if (surface) {
    for (let k = 0; k < count; k++) clearance = Math.max(clearance, raw[k] - surface(dth[k], dyy[k]))
  }

  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= cols; i++) {
      const k = j * (cols + 1) + i
      const theta = angle + dth[k]
      const y = placement.centerY + dyy[k]
      const r = (surface ? surface(dth[k], dyy[k]) + clearance : raw[k]) + lift
      position[k * 3] = cx + Math.sin(theta) * r
      position[k * 3 + 1] = y
      position[k * 3 + 2] = cz + Math.cos(theta) * r
    }
  }
  return position
}

/**
 * Least-squares quadratic surface through the sampled radii, in the sheet's own
 * (angle, height) frame. Returns null when the fit is degenerate — a tiny grid, or a
 * part with no usable samples — in which case the caller falls back to raw sampling.
 */
function fitRadiusPatch(
  dth: Float64Array,
  dy: Float64Array,
  r: Float64Array
): ((t: number, y: number) => number) | null {
  const N = 6
  if (r.length < N * 2) return null
  // Normalise both axes to roughly [-1,1] so the normal equations stay well conditioned.
  let ts = 0
  let ys = 0
  for (let k = 0; k < r.length; k++) {
    ts = Math.max(ts, Math.abs(dth[k]))
    ys = Math.max(ys, Math.abs(dy[k]))
  }
  ts = ts || 1
  ys = ys || 1

  const f = new Float64Array(N)
  const basis = (t: number, y: number) => {
    const a = t / ts
    const b = y / ys
    f[0] = 1
    f[1] = a
    f[2] = b
    f[3] = a * a
    f[4] = b * b
    f[5] = a * b
  }

  const A = new Float64Array(N * N)
  const rhs = new Float64Array(N)
  for (let k = 0; k < r.length; k++) {
    basis(dth[k], dy[k])
    for (let a = 0; a < N; a++) {
      rhs[a] += f[a] * r[k]
      for (let c = 0; c < N; c++) A[a * N + c] += f[a] * f[c]
    }
  }

  const coeff = solveSymmetric(A, rhs, N)
  if (!coeff) return null
  return (t: number, y: number) => {
    basis(t, y)
    let sum = 0
    for (let a = 0; a < N; a++) sum += coeff[a] * f[a]
    return sum
  }
}

/** Gaussian elimination with partial pivoting; null if the system is singular. */
function solveSymmetric(A: Float64Array, b: Float64Array, n: number): Float64Array | null {
  const m = Float64Array.from(A)
  const x = Float64Array.from(b)
  for (let col = 0; col < n; col++) {
    let pivot = col
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(m[row * n + col]) > Math.abs(m[pivot * n + col])) pivot = row
    }
    if (Math.abs(m[pivot * n + col]) < 1e-12) return null
    if (pivot !== col) {
      for (let c = 0; c < n; c++) {
        const t = m[col * n + c]
        m[col * n + c] = m[pivot * n + c]
        m[pivot * n + c] = t
      }
      const t = x[col]
      x[col] = x[pivot]
      x[pivot] = t
    }
    for (let row = col + 1; row < n; row++) {
      const factor = m[row * n + col] / m[col * n + col]
      if (!factor) continue
      for (let c = col; c < n; c++) m[row * n + c] -= factor * m[col * n + c]
      x[row] -= factor * x[col]
    }
  }
  for (let row = n - 1; row >= 0; row--) {
    let sum = x[row]
    for (let c = row + 1; c < n; c++) sum -= m[row * n + c] * x[c]
    x[row] = sum / m[row * n + row]
  }
  return x
}

/** Outward normals from the grid tangents (see the winding note in gridIndices). */
function gridNormals(p: Float32Array, cols: number, rows: number): Float32Array {
  const n = new Float32Array(p.length)
  const at = (i: number, j: number) => (j * (cols + 1) + i) * 3
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= cols; i++) {
      const i0 = Math.max(0, i - 1)
      const i1 = Math.min(cols, i + 1)
      const j0 = Math.max(0, j - 1)
      const j1 = Math.min(rows, j + 1)
      const du = [p[at(i1, j)] - p[at(i0, j)], p[at(i1, j) + 1] - p[at(i0, j) + 1], p[at(i1, j) + 2] - p[at(i0, j) + 2]]
      const dv = [p[at(i, j1)] - p[at(i, j0)], p[at(i, j1) + 1] - p[at(i, j0) + 1], p[at(i, j1) + 2] - p[at(i, j0) + 2]]
      // dv runs DOWN the sheet, so cross(dv, du) points away from the surface.
      let nx = dv[1] * du[2] - dv[2] * du[1]
      let ny = dv[2] * du[0] - dv[0] * du[2]
      let nz = dv[0] * du[1] - dv[1] * du[0]
      const len = Math.hypot(nx, ny, nz) || 1
      nx /= len
      ny /= len
      nz /= len
      const k = at(i, j)
      n[k] = nx
      n[k + 1] = ny
      n[k + 2] = nz
    }
  }
  return n
}

/**
 * Triangles for a (cols x rows) grid. Vertex order goes right (+i) and down (+j) as
 * seen from outside, so `(a, c, b)` / `(b, c, d)` is counter-clockwise = front-facing.
 */
function gridIndices(cols: number, rows: number, base: number, flip: boolean): Uint32Array {
  const idx = new Uint32Array(cols * rows * 6)
  let o = 0
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const a = base + j * (cols + 1) + i
      const b = a + 1
      const c = a + (cols + 1)
      const d = c + 1
      if (flip) {
        idx[o++] = a
        idx[o++] = b
        idx[o++] = c
        idx[o++] = b
        idx[o++] = d
        idx[o++] = c
      } else {
        idx[o++] = a
        idx[o++] = c
        idx[o++] = b
        idx[o++] = b
        idx[o++] = c
        idx[o++] = d
      }
    }
  }
  return idx
}

/**
 * Side wall of an extruded logo: a quad strip around the border joining the outer
 * grid to the inner one. Rim vertices reuse the border UVs, so the (normally
 * transparent) edge of the artwork cuts the rim away too.
 */
function buildRim(
  position: Float32Array,
  normal: Float32Array,
  uv: Float32Array,
  cols: number,
  rows: number,
  vCount: number
): LogoMeshData {
  const border: number[] = []
  for (let i = 0; i <= cols; i++) border.push(i) // top, left→right
  for (let j = 1; j <= rows; j++) border.push(j * (cols + 1) + cols) // right, top→bottom
  for (let i = cols - 1; i >= 0; i--) border.push(rows * (cols + 1) + i) // bottom, right→left
  for (let j = rows - 1; j >= 1; j--) border.push(j * (cols + 1)) // left, bottom→top

  const n = border.length
  const pos = new Float32Array(n * 2 * 3)
  const nor = new Float32Array(n * 2 * 3)
  const uvs = new Float32Array(n * 2 * 2)

  for (let k = 0; k < n; k++) {
    const outerV = border[k]
    const innerV = vCount + border[k]
    for (const [slot, src] of [
      [k, outerV],
      [n + k, innerV],
    ] as const) {
      pos[slot * 3] = position[src * 3]
      pos[slot * 3 + 1] = position[src * 3 + 1]
      pos[slot * 3 + 2] = position[src * 3 + 2]
      uvs[slot * 2] = uv[src * 2]
      uvs[slot * 2 + 1] = uv[src * 2 + 1]
    }
    // Rim normal: perpendicular to the sheet normal, pointing away from the border.
    const prev = border[(k - 1 + n) % n]
    const next = border[(k + 1) % n]
    const ex = position[next * 3] - position[prev * 3]
    const ey = position[next * 3 + 1] - position[prev * 3 + 1]
    const ez = position[next * 3 + 2] - position[prev * 3 + 2]
    const sx = normal[outerV * 3]
    const sy = normal[outerV * 3 + 1]
    const sz = normal[outerV * 3 + 2]
    let rx = ey * sz - ez * sy
    let ry = ez * sx - ex * sz
    let rz = ex * sy - ey * sx
    const len = Math.hypot(rx, ry, rz) || 1
    rx /= len
    ry /= len
    rz /= len
    for (const slot of [k, n + k]) {
      nor[slot * 3] = rx
      nor[slot * 3 + 1] = ry
      nor[slot * 3 + 2] = rz
    }
  }

  const idx = new Uint32Array(n * 6)
  let o = 0
  const base = vCount * 2
  for (let k = 0; k < n; k++) {
    const k2 = (k + 1) % n
    const a = base + k
    const b = base + k2
    const c = base + n + k
    const d = base + n + k2
    idx[o++] = a
    idx[o++] = c
    idx[o++] = b
    idx[o++] = b
    idx[o++] = c
    idx[o++] = d
  }

  return { position: pos, normal: nor, uv: uvs, index: idx }
}

/**
 * Relax the shading normals. They come from differences between neighbouring samples of
 * a real, slightly rough mesh, so they carry its roughness — and on a decal that shows
 * up as uneven brightness across the strokes, which reads as wobbly lettering. The
 * sheet is a smooth patch, so averaging each normal with its neighbours costs nothing
 * and makes the artwork light evenly.
 */
function smoothGridNormals(n: Float32Array, cols: number, rows: number, passes = 2): void {
  const at = (i: number, j: number) => (j * (cols + 1) + i) * 3
  const tmp = new Float32Array(n.length)
  for (let pass = 0; pass < passes; pass++) {
    tmp.set(n)
    for (let j = 0; j <= rows; j++) {
      for (let i = 0; i <= cols; i++) {
        let x = 0
        let y = 0
        let z = 0
        for (const [di, dj] of NEIGHBOURS) {
          const ni = Math.max(0, Math.min(cols, i + di))
          const nj = Math.max(0, Math.min(rows, j + dj))
          const k = at(ni, nj)
          x += tmp[k]
          y += tmp[k + 1]
          z += tmp[k + 2]
        }
        const len = Math.hypot(x, y, z) || 1
        const k = at(i, j)
        n[k] = x / len
        n[k + 1] = y / len
        n[k + 2] = z / len
      }
    }
  }
}

const NEIGHBOURS: [number, number][] = [
  [0, 0],
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
]

function concatF32(a: Float32Array, b: Float32Array): Float32Array {
  const out = new Float32Array(a.length + b.length)
  out.set(a, 0)
  out.set(b, a.length)
  return out
}

function concatU32(a: Uint32Array, b: Uint32Array): Uint32Array {
  const out = new Uint32Array(a.length + b.length)
  out.set(a, 0)
  out.set(b, a.length)
  return out
}

/** Column-major translation matrix, used as a logo part's export node transform. */
function translationMatrix(offset: [number, number, number]): number[] {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, offset[0], offset[1], offset[2], 1]
}

/**
 * Build (or rebuild) a logo part from its placement. The geometry lives in preview
 * space like every other part; `sourceTransform` carries the loader's centering
 * offset so the faithful GLB re-export puts it back in the source file's frame.
 */
export function buildLogoObject(
  id: string,
  placement: LogoPlacement,
  objects: AppObject[],
  sourceOffset: [number, number, number] | undefined,
  previous?: AppObject
): AppObject {
  const target = logoTargetOf(objects, placement)
  const field = target ? surfaceFieldOf(target) : null
  // Lift it clear of anything else at that spot, so the sheet is never sealed inside the
  // product. The stored placement keeps the user's own standoff; this is only for building.
  const lift = clearanceLift(objects, placement, target)
  const placed = lift > 0 ? { ...placement, standoff: placement.standoff + lift } : placement
  // Cut to the artwork's outline when asked and the trace is ready (ensureLogoOutline is
  // awaited by the caller before placing or re-placing a logo). A miss, or artwork nothing
  // could be traced from, falls back to the rectangular sheet.
  const outline = placed.cutToShape ? cachedLogoOutline(placed.image) : null
  const data = (outline && buildOutlineMeshData(placed, field, outline)) || buildLogoMeshData(placed, field)

  const mesh: OcctImportMesh = {
    id,
    color: [1, 1, 1],
    attributes: {
      position: { array: data.position },
      normal: { array: data.normal },
      uv: { array: data.uv },
    },
    index: { array: data.index },
  }

  return {
    ...previous,
    id,
    color: [1, 1, 1],
    mesh,
    logo: placement,
    // No material of its own: a new logo is just a mesh, and follows the shared material
    // like any other part until the user gives it one. A rebuild (placement edit) keeps
    // whatever material the logo has by then, via the spread of `previous` above.
    // The sheet is generated at its final place, so it needs no extra move offset.
    offset: previous?.offset,
    sourceAttributes: {
      position: new Float32Array(data.position),
      normal: new Float32Array(data.normal),
      uv: new Float32Array(data.uv),
      index: new Uint32Array(data.index),
    },
    sourceTransform: translationMatrix(sourceOffset ?? [0, 0, 0]),
  }
}

/**
 * Sensible first placement for a freshly uploaded logo: front-facing, centered on the
 * body, about half its diameter wide and kept at the artwork's aspect ratio.
 */
export function defaultLogoPlacement(
  objects: AppObject[],
  image: string,
  imageAspect: number,
  mmScale: number,
  knockoutWhite = false
): LogoPlacement {
  // Mid-height of the whole product, then the part that is outermost there — the face a
  // logo belongs on. Picking the biggest part instead lands it on a vacuum flask's liner.
  const candidates = objects.filter((o) => !o.logo)
  const bodyFields = candidates.map((o) => surfaceFieldOf(o)).filter((f): f is SurfaceField => !!f)
  const midY = bodyFields.length
    ? (Math.min(...bodyFields.map((f) => f.minY)) + Math.max(...bodyFields.map((f) => f.maxY))) / 2
    : 0
  const target = outermostPartAt(candidates, midY)
  const field = target ? surfaceFieldOf(target) : null
  const centerY = field ? (field.minY + field.maxY) / 2 : 0
  const radius = field ? radiusAt(field, 0, centerY) : 1
  const bodyHeight = field ? field.maxY - field.minY : 2

  let width = radius
  let height = width / Math.max(imageAspect, 1e-3)
  const maxHeight = bodyHeight * 0.5
  if (height > maxHeight && height > 0) {
    const k = maxHeight / height
    width *= k
    height *= k
  }

  return {
    image,
    targetId: target?.id,
    angleDeg: 0,
    centerY,
    width,
    height,
    rotationDeg: 0,
    // 0.4 mm off the surface — invisible on a product, but a wide enough gap that the
    // exported GLB does not z-fight in viewers with a coarser depth buffer than ours.
    standoff: 0.4 / (mmScale || 1),
    thickness: 0,
    conform: true,
    // The mark's own shape, not a rectangle that only looks like it where alpha is read.
    cutToShape: true,
    segments: DEFAULT_SEGMENTS,
    imageAspect,
    knockoutWhite,
  }
}

/** Angle (deg) / height a click on the model maps to, for click-to-place. */
export function placementFromPoint(
  objects: AppObject[],
  placement: LogoPlacement,
  point: { x: number; y: number; z: number }
): { angleDeg: number; centerY: number } | null {
  const target = logoTargetOf(objects, placement)
  const field = target ? surfaceFieldOf(target) : null
  if (!field) return null
  return {
    angleDeg: Math.atan2(point.x - field.cx, point.z - field.cz) / DEG,
    centerY: point.y,
  }
}

/**
 * Logo placement, written into the exported glTF node's `extras` and read back on import.
 *
 * Without it a re-imported logo is just another mesh: its artwork gets recovered as a
 * printed pattern, flattened over an opaque base colour, and the cut-out becomes a solid
 * card. With it the part comes back as a real logo — same placement, still editable.
 *
 * Lengths are stored in MILLIMETRES rather than model units, because the exported file's
 * unit is not the one the placement was authored in (a STEP model works in millimetres
 * and exports as metres). Millimetres are the one scale both ends agree on.
 *
 * The artwork itself is deliberately NOT stored here — it is already in the file as the
 * material's texture, and a second copy as a data URL would add megabytes to the JSON.
 * Import recovers it from the map.
 */
export interface LogoExtras {
  version: 1
  targetId?: string
  angleDeg: number
  centerYMm: number
  widthMm: number
  heightMm: number
  rotationDeg: number
  standoffMm: number
  thicknessMm: number
  conform: boolean
  segments?: number
  imageAspect?: number
  /** Whether the sheet was cut to the artwork's outline, so a re-import rebuilds it the same. */
  cutToShape?: boolean
}

export function toLogoExtras(placement: LogoPlacement, mmPerUnit: number): LogoExtras {
  return {
    version: 1,
    targetId: placement.targetId,
    angleDeg: placement.angleDeg,
    centerYMm: placement.centerY * mmPerUnit,
    widthMm: placement.width * mmPerUnit,
    heightMm: placement.height * mmPerUnit,
    rotationDeg: placement.rotationDeg,
    standoffMm: placement.standoff * mmPerUnit,
    thicknessMm: placement.thickness * mmPerUnit,
    conform: placement.conform,
    segments: placement.segments,
    imageAspect: placement.imageAspect,
    cutToShape: placement.cutToShape,
  }
}

/** True when a glTF node's extras carry a placement this app wrote. */
export function isLogoExtras(value: unknown): value is LogoExtras {
  const v = value as LogoExtras | null
  return !!v && v.version === 1 && typeof v.centerYMm === 'number' && typeof v.widthMm === 'number'
}

export function fromLogoExtras(extras: LogoExtras, mmPerUnit: number, image: string): LogoPlacement {
  const toUnits = (mm: number) => mm / (mmPerUnit || 1)
  return {
    image,
    targetId: extras.targetId,
    angleDeg: extras.angleDeg,
    centerY: toUnits(extras.centerYMm),
    width: toUnits(extras.widthMm),
    height: toUnits(extras.heightMm),
    rotationDeg: extras.rotationDeg,
    standoff: toUnits(extras.standoffMm),
    thickness: toUnits(extras.thicknessMm),
    conform: extras.conform,
    segments: extras.segments,
    imageAspect: extras.imageAspect,
    cutToShape: extras.cutToShape,
    // The recovered artwork already carries the alpha the knockout produced; running it
    // again would be cutting an already-cut image.
    knockoutWhite: false,
  }
}

/** Next free `logo_N` id (or `logo_main` when the model has no logo part yet). */
export function nextLogoId(objects: AppObject[]): string {
  const taken = new Set(objects.map((o) => o.id.toLowerCase()))
  if (!taken.has('logo_main') && !objects.some((o) => /logo/i.test(o.id))) return 'logo_main'
  for (let n = 1; n < 1000; n++) {
    const id = `logo_${n}`
    if (!taken.has(id)) return id
  }
  return `logo_${objects.length + 1}`
}
